import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ArrowUpDown, Scale } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  AccreditationTerm,
  TERM_BADGE_CLASSES,
  TERM_LABELS,
} from "@/components/Accreditation/types";

type Journalist = {
  id: string;
  first_name: string;
  last_name: string;
  nationality: string | null;
  seniority: string;
  status: string;
  beats: string[];
  languages: string[];
};

type Engagement = { journalist_id: string; engagement_type: string };

const MAX_SELECTED = 3;

const STATUS_BADGE: Record<string, string> = {
  active: "bg-green-100 text-green-800 border-green-300",
  inactive: "bg-gray-100 text-gray-600 border-gray-300",
  blacklisted: "bg-red-100 text-red-800 border-red-300",
};

const PASS_STATUS_BADGE: Record<string, string> = {
  active: "bg-green-100 text-green-800 border-green-300",
  expired: "bg-gray-100 text-gray-600 border-gray-300",
  revoked: "bg-red-100 text-red-800 border-red-300",
};

type SortKey = "name" | "engagements" | "status";

interface JournalistComparisonTabProps {
  orgId: string;
  journalists: Journalist[];
  engagements: Engagement[];
}

export function JournalistComparisonTab({
  orgId,
  journalists,
  engagements,
}: JournalistComparisonTabProps) {
  const { data: outletByJournalist } = useQuery({
    queryKey: ["journalist_comparison_outlets", orgId],
    queryFn: async () => {
      const { data } = await supabase
        .from("journalist_affiliations" as any)
        .select("journalist_id, is_primary, media_outlets(name)")
        .eq("org_id", orgId)
        .eq("is_primary", true);
      const m = new Map<string, string | null>();
      (data as any[] ?? []).forEach((a) => m.set(a.journalist_id, a.media_outlets?.name ?? null));
      return m;
    },
    enabled: !!orgId,
  });
  const outletMap = outletByJournalist ?? new Map<string, string | null>();
  const [selected, setSelected] = useState<string[]>([]);
  const [sortKey, setSortKey] = useState<SortKey>("engagements");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [compareOpen, setCompareOpen] = useState(false);

  const engagementCounts = useMemo(() => {
    const m = new Map<string, number>();
    engagements.forEach((e) => m.set(e.journalist_id, (m.get(e.journalist_id) ?? 0) + 1));
    return m;
  }, [engagements]);

  const { data: accreditationInfo } = useQuery({
    queryKey: ["journalist_comparison_accreditation", orgId],
    queryFn: async () => {
      const [passesRes, requestsRes] = await Promise.all([
        supabase
          .from("press_passes" as any)
          .select("journalist_id, status, accreditation_term, valid_from, valid_to")
          .eq("org_id", orgId)
          .order("issued_at", { ascending: false }),
        supabase
          .from("accreditation_requests" as any)
          .select("journalist_id, status, accreditation_term, created_at")
          .eq("org_id", orgId)
          .order("created_at", { ascending: false }),
      ]);
      const passByJournalist = new Map<string, any>();
      (passesRes.data as any[] ?? []).forEach((p) => {
        if (!passByJournalist.has(p.journalist_id)) passByJournalist.set(p.journalist_id, p);
      });
      const requestByJournalist = new Map<string, any>();
      (requestsRes.data as any[] ?? []).forEach((r) => {
        if (!requestByJournalist.has(r.journalist_id)) requestByJournalist.set(r.journalist_id, r);
      });
      return { passByJournalist, requestByJournalist };
    },
    enabled: !!orgId,
  });

  const rows = useMemo(() => {
    return journalists.map((j) => {
      const pass = accreditationInfo?.passByJournalist.get(j.id);
      const req = accreditationInfo?.requestByJournalist.get(j.id);
      return {
        journalist: j,
        outlet: outletMap.get(j.id) ?? null,
        engagementCount: engagementCounts.get(j.id) ?? 0,
        passStatus: pass?.status as string | undefined,
        term: (pass?.accreditation_term ?? req?.accreditation_term) as AccreditationTerm | undefined,
        requestStatus: req?.status as string | undefined,
      };
    });
  }, [journalists, accreditationInfo, engagementCounts, outletByJournalist]);

  const sortedRows = useMemo(() => {
    const sorted = [...rows].sort((a, b) => {
      let cmp = 0;
      if (sortKey === "name") {
        cmp = `${a.journalist.first_name} ${a.journalist.last_name}`.localeCompare(
          `${b.journalist.first_name} ${b.journalist.last_name}`
        );
      } else if (sortKey === "engagements") {
        cmp = a.engagementCount - b.engagementCount;
      } else if (sortKey === "status") {
        cmp = (a.journalist.status ?? "").localeCompare(b.journalist.status ?? "");
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
    return sorted;
  }, [rows, sortKey, sortDir]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  }

  function toggleSelected(id: string) {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= MAX_SELECTED) return prev;
      return [...prev, id];
    });
  }

  const selectedRows = sortedRows.filter((r) => selected.includes(r.journalist.id));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-justify text-sm text-muted-foreground">
          Select up to {MAX_SELECTED} journalists to compare side by side, or sort the leaderboard
          below by activity.
        </p>
        <Button
          size="sm"
          disabled={selected.length < 2}
          onClick={() => setCompareOpen(true)}
          style={{ backgroundColor: "#006B3C" }}
          className="text-white hover:opacity-90"
        >
          <Scale className="w-4 h-4 mr-2" />
          Compare ({selected.length})
        </Button>
      </div>

      <div className="rounded-lg border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow style={{ backgroundColor: "#0D2240" }}>
              <TableHead style={{ color: "#F0E4B8" }} className="w-10" />
              <SortableHead label="Journalist" active={sortKey === "name"} dir={sortDir} onClick={() => toggleSort("name")} />
              <TableHead style={{ color: "#F0E4B8" }} className="font-semibold">Outlet</TableHead>
              <TableHead style={{ color: "#F0E4B8" }} className="font-semibold">Term</TableHead>
              <TableHead style={{ color: "#F0E4B8" }} className="font-semibold">Pass status</TableHead>
              <SortableHead label="Engagements" active={sortKey === "engagements"} dir={sortDir} onClick={() => toggleSort("engagements")} />
              <SortableHead label="Status" active={sortKey === "status"} dir={sortDir} onClick={() => toggleSort("status")} />
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                  No journalists in the registry yet.
                </TableCell>
              </TableRow>
            ) : (
              sortedRows.map((r, idx) => (
                <TableRow key={r.journalist.id} className={idx % 2 === 1 ? "bg-[#F7F5F0]" : ""}>
                  <TableCell>
                    <Checkbox
                      checked={selected.includes(r.journalist.id)}
                      disabled={!selected.includes(r.journalist.id) && selected.length >= MAX_SELECTED}
                      onCheckedChange={() => toggleSelected(r.journalist.id)}
                    />
                  </TableCell>
                  <TableCell className="font-medium">
                    {r.journalist.first_name} {r.journalist.last_name}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{r.outlet ?? "—"}</TableCell>
                  <TableCell>
                    {r.term ? (
                      <Badge variant="outline" className={TERM_BADGE_CLASSES[r.term]}>
                        {TERM_LABELS[r.term]}
                      </Badge>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>
                    {r.passStatus ? (
                      <Badge variant="outline" className={PASS_STATUS_BADGE[r.passStatus] ?? ""}>
                        {r.passStatus}
                      </Badge>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell className="font-semibold">{r.engagementCount}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={STATUS_BADGE[r.journalist.status] ?? ""}>
                      {r.journalist.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Journalist comparison</DialogTitle>
          </DialogHeader>
          <div
            className="grid gap-4"
            style={{ gridTemplateColumns: `repeat(${selectedRows.length}, minmax(0, 1fr))` }}
          >
            {selectedRows.map((r) => (
              <Card key={r.journalist.id}>
                <CardContent className="pt-6 space-y-3 text-sm">
                  <div>
                    <p className="text-justify font-semibold text-base">
                      {r.journalist.first_name} {r.journalist.last_name}
                    </p>
                    <p className="text-justify text-xs text-muted-foreground">{r.outlet ?? "No outlet on file"}</p>
                  </div>
                  <CompareField label="Status">
                    <Badge variant="outline" className={STATUS_BADGE[r.journalist.status] ?? ""}>
                      {r.journalist.status}
                    </Badge>
                  </CompareField>
                  <CompareField label="Seniority">
                    <span className="capitalize">{r.journalist.seniority?.replace(/_/g, " ")}</span>
                  </CompareField>
                  <CompareField label="Nationality">{r.journalist.nationality ?? "—"}</CompareField>
                  <CompareField label="Languages">{(r.journalist.languages ?? []).join(", ") || "—"}</CompareField>
                  <CompareField label="Beats">{(r.journalist.beats ?? []).join(", ") || "—"}</CompareField>
                  <CompareField label="Accreditation term">
                    {r.term ? (
                      <Badge variant="outline" className={TERM_BADGE_CLASSES[r.term]}>
                        {TERM_LABELS[r.term]}
                      </Badge>
                    ) : (
                      "—"
                    )}
                  </CompareField>
                  <CompareField label="Pass status">
                    {r.passStatus ? (
                      <Badge variant="outline" className={PASS_STATUS_BADGE[r.passStatus] ?? ""}>
                        {r.passStatus}
                      </Badge>
                    ) : (
                      "—"
                    )}
                  </CompareField>
                  <CompareField label="Engagements logged">
                    <span className="font-semibold">{r.engagementCount}</span>
                  </CompareField>
                </CardContent>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SortableHead({
  label,
  active,
  dir,
  onClick,
}: {
  label: string;
  active: boolean;
  dir: "asc" | "desc";
  onClick: () => void;
}) {
  return (
    <TableHead style={{ color: "#F0E4B8" }} className="font-semibold cursor-pointer select-none" onClick={onClick}>
      <span className="inline-flex items-center gap-1">
        {label}
        <ArrowUpDown className={`w-3 h-3 ${active ? "opacity-100" : "opacity-40"}`} />
        {active && <span className="text-[10px]">{dir === "asc" ? "↑" : "↓"}</span>}
      </span>
    </TableHead>
  );
}

function CompareField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2 border-t pt-2 first:border-t-0 first:pt-0">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-right">{children}</span>
    </div>
  );
}
